import mixpanel from '@disco/api/mixpanel'
import { SOURCE_SCOPE } from '@disco/constants/search'
import captureError from '@disco/utils/captureError'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'

import type { Track } from 'types/track'

export const OPEN_RIGHTS_TAB_EVENT = 'Open rights tab'

export type RightsTypeLabel = 'Publishing' | 'Recording'
export type AvailableRightsTypes = RightsTypeLabel | 'Both'

/** The parts of a track the MDS event properties are read from */
export type MdsEventTrack = Pick<
  Track,
  'internal_data' | 'origin' | 'scope' | 'source'
>

export interface OpenRightsTabArgs {
  rightsType: RightsTypeLabel
  trigger: 'Default' | 'Switch'
  availableRightsTypes: AvailableRightsTypes
  /** Original track, used for the MDS catalog properties */
  track?: MdsEventTrack | null
  trackId?: number | null
  /** Set for bulk edit, where splits aren't loaded */
  bulkTrackCount?: number
  hasData: boolean
  splitCount: number
}

const isMdsTrack = (track?: MdsEventTrack | null): boolean =>
  isAggregatedSearchTrack(track) || track?.scope === SOURCE_SCOPE.MDS

export const buildOpenRightsTabProps = ({
  rightsType,
  trigger,
  availableRightsTypes,
  track,
  trackId,
  bulkTrackCount,
  hasData,
  splitCount,
}: OpenRightsTabArgs): Record<string, unknown> => {
  const isBulk = bulkTrackCount !== undefined
  return {
    'Rights type': rightsType,
    Trigger: trigger,
    'Available rights types': availableRightsTypes,
    ...(isBulk
      ? { Bulk: true, 'Track count': bulkTrackCount }
      : {
          'Track ID': trackId,
          'Has data': hasData,
          'Split count': splitCount,
        }),
    // Matches the DISCO-5985 properties on the other MDS events. V2 search
    // hits have no `origin`, so their catalog comes from `source` instead.
    ...(isMdsTrack(track)
      ? {
          Context: 'Aggregated search',
          Catalog: track?.origin?.source_library?.id ?? track?.source?.id,
          'Catalog Business':
            track?.origin?.business?.id ?? track?.source?.business_id,
        }
      : {}),
  }
}

/** Never throws: a tracking failure must not break the Rights tab. */
export const sendOpenRightsTabEvent = (args: OpenRightsTabArgs): void => {
  try {
    mixpanel.sendPureEvent(OPEN_RIGHTS_TAB_EVENT, buildOpenRightsTabProps(args))
  } catch (error) {
    captureError(error as Error, { moduleName: 'RightsMixpanel' })
  }
}
